<template>
  <div
    class="c-population-tab-partners-row w-full p-4"
    :class="{ 'c-population-tab-partners-row--modified': hasChanges }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div
      class="grid grid-cols-[minmax(250px,2.5fr)_minmax(240px,1.5fr)_minmax(240px,1.5fr)_minmax(120px,1.5fr)_110px] gap-4 items-center w-full"
    >
      <div
        class="c-population-tab-partners-row__name-cell flex items-center gap-8 px-12 min-h-[48px]"
        :style="nameCellStyle"
      >
        <BittsCheckbox
          v-if="canEdit"
          class="mr-8"
          :checked="isPartnerSelected(item.id)"
          :aria-label="selectCheckboxLabel"
          @input="(checked: boolean) => handleCheckboxChange(item.id, checked)"
        />
        <BittsAvatar
          size="small"
          shape="square"
          :show-border="true"
          :org="item"
        />
        <span class="text-neutral-text-strong font-bold truncate min-w-0">
          {{ item.name }}
        </span>
      </div>
      <div class="px-12 flex items-center min-w-0" @click.stop>
        <SharingSettingsVisibilitySelector
          :initial-visibility="item.currentVisibility"
          :aria-label="sharingLevelLabel"
          :disabled="!canEdit"
          :is-greenfield-sharing-blocked="isGreenfieldSharingBlocked"
          :disabled-option-content="{
            text: 'This sharing option was disabled. Please reach out to your admins.',
          }"
          @change="
            (visibility: SharingType) =>
              handleVisibilityChange(item.id, visibility)
          "
        />
      </div>
      <div class="px-12 flex items-center min-w-0" @click.stop>
        <SharingSettingsPresetSelector
          v-if="shouldShowPresetSelector"
          :initial-selected-preset-id="localSelectedPresetId"
          :aria-label="fieldPresetsLabel"
          :preset-options="presetOptions"
          :feed-id="feedId"
          :source-id="sourceId"
          :disabled="!canEdit"
          :can-edit-presets="false"
          :is-compact="true"
          :external-preset-fields-data="sharedPresetFieldsData"
          @change="handlePresetChange"
          @open-preset-modal="handleOpenPresetModal"
        />
      </div>
      <div class="px-12 flex items-center">
        <div class="flex gap-4 flex-wrap">
          <BittsTag
            v-for="tag in item.tags?.slice(0, 2)"
            :key="String(tag.id)"
            :text="tag.label"
            size="x-small"
          />
          <BittsTooltip
            v-if="item.tags && item.tags.length > 2"
            placement="bottomLeft"
            :mouse-enter-delay="0.3"
          >
            <span class="c-population-tab-partners-row__tag">
              +{{ item.tags.length - 2 }}
            </span>
            <template #title>
              {{
                item.tags
                  .slice(2)
                  .map((tag) => tag.label)
                  .join(', ')
              }}
            </template>
          </BittsTooltip>
        </div>
      </div>
      <div class="px-12 flex items-center justify-end gap-8" @click.stop>
        <BittsTooltip
          v-if="item.overridesCount && item.overridesCount > 0"
          placement="topLeft"
          :mouse-enter-delay="0.3"
        >
          <div class="relative inline-block">
            <BittsButton
              aria-label="Review overrides"
              size="x-small"
              type="neutral"
              variant="outline"
              :center-icon="['fak', 'override']"
              @click="handleOpenPartnerSettings(true)"
            />
            <div class="absolute -top-8 -right-6 z-10">
              <BittsCount type="neutral" :count="item.overridesCount" />
            </div>
          </div>
          <template #title> Review Overrides </template>
        </BittsTooltip>
        <BittsTooltip
          v-if="item.isCustom && canEdit"
          placement="topLeft"
          :mouse-enter-delay="0.3"
        >
          <BittsButton
            data-testid="restore-button"
            aria-label="Restore default sharing settings"
            size="x-small"
            type="neutral"
            variant="outline"
            :center-icon="['fak', 'restore']"
            @click="handleRestoreToDefault"
          />
          <template #title>Restore default sharing settings</template>
        </BittsTooltip>
        <BittsTooltip placement="topLeft" :mouse-enter-delay="0.3">
          <BittsButton
            aria-label="Open partner sharing settings"
            size="x-small"
            type="neutral"
            variant="outline"
            :center-icon="['fak', 'open-link']"
            @click="handleOpenPartnerSettings()"
          />
          <template #title>Open partner sharing settings</template>
        </BittsTooltip>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsCheckbox,
  BittsCount,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';

import { computed, ref, toRef, watch } from 'vue';

import type { PresetFieldsDataMap } from '@/composables/dataSharingHub/usePresetFieldsData';
import type { PartnerWithSharing } from '@/composables/dataSharingHub/useSharingSettingsHub';
import { useFieldPresetsModal } from '@/composables/useFieldPresetsModal';
import {
  GREENFIELD_SHARING,
  HIDDEN,
  OLAPS_ONLY,
  SHARING,
  type SharingType,
} from '@/constants/data_shares';
import { useDataSharePresetsStore } from '@/stores';
import type { PresetOptionsGroup } from '@/types/data_share_presets';

import SharingSettingsPresetSelector from '../SharingSettingsPresetSelector.vue';
import SharingSettingsVisibilitySelector from '../SharingSettingsVisibilitySelector.vue';

import useStickyNameCell from './useStickyNameCell';

type EnrichedPartner = PartnerWithSharing & {
  currentVisibility?: SharingType;
  currentPresetId?: number | null;
};

const props = withDefaults(
  defineProps<{
    item: EnrichedPartner;
    selectedPartnerIdsSet: Set<number>;
    feedId?: number;
    sourceId?: number;
    hasChanges?: boolean;
    canEdit?: boolean;
    isGreenfieldSharingBlocked?: boolean;
    sharedBasePresetOptions?: PresetOptionsGroup[];
    sharedPresetFieldsData?: PresetFieldsDataMap;
  }>(),
  {
    feedId: undefined,
    sourceId: undefined,
    canEdit: true,
    isGreenfieldSharingBlocked: false,
    sharedBasePresetOptions: undefined,
    sharedPresetFieldsData: undefined,
  },
);

const emit = defineEmits<{
  'checkbox-change': [partnerId: number, checked: boolean];
  'visibility-change': [partnerId: number, visibility: SharingType];
  'preset-change': [partnerId: number, presetId: number | undefined];
  'open-partner-settings': [
    partnerId: number,
    overridesFilter: boolean | undefined,
  ];
  'restore-to-default': [partnerId: number];
}>();
const dataSharePresetsStore = useDataSharePresetsStore();
const { openModal } = useFieldPresetsModal();

const localSelectedPresetId = ref<number | null>(
  props.item.currentPresetId ?? null,
);

const localVisibility = ref<SharingType | undefined>(
  props.item.currentVisibility,
);

watch(
  () => props.item.currentPresetId,
  (newPresetId) => {
    localSelectedPresetId.value = newPresetId ?? null;
  },
);

watch(
  () => props.item.currentVisibility,
  (newVisibility) => {
    localVisibility.value = newVisibility;
  },
);

watch(localVisibility, (newVisibility, oldVisibility) => {
  const switchingToSharing =
    (newVisibility === SHARING || newVisibility === GREENFIELD_SHARING) &&
    (oldVisibility === HIDDEN ||
      oldVisibility === OLAPS_ONLY ||
      !oldVisibility);

  const switchingFromSharing =
    (oldVisibility === SHARING || oldVisibility === GREENFIELD_SHARING) &&
    (newVisibility === HIDDEN || newVisibility === OLAPS_ONLY);

  if (switchingToSharing && !localSelectedPresetId.value) {
    const defaultPresetId = dataSharePresetsStore.getDefaultOptionId(
      basePresetOptions.value,
    );
    if (defaultPresetId !== null) {
      localSelectedPresetId.value = defaultPresetId;
      handlePresetChange(defaultPresetId);
    }
  }

  if (switchingFromSharing) {
    const originalPresetId = props.item.presetInfo?.presetId ?? undefined;
    const currentPresetId = localSelectedPresetId.value;
    const targetPresetId = originalPresetId ?? null;
    if (currentPresetId !== targetPresetId) {
      localSelectedPresetId.value = targetPresetId;
      handlePresetChange(originalPresetId);
    }
  }
});

const selectedSingleSharePreset = computed(() => {
  if (!props.feedId || !localSelectedPresetId.value) return null;

  const filteredPresets = dataSharePresetsStore.getFilteredPresetsByFeeId(
    props.feedId,
    props.sourceId,
  );

  return filteredPresets.find(
    (preset) =>
      preset.id === localSelectedPresetId.value &&
      preset.preset_type === 'single_share_preset',
  );
});

const basePresetOptions = computed((): PresetOptionsGroup[] => {
  if (props.sharedBasePresetOptions) return props.sharedBasePresetOptions;
  if (!props.feedId) return [];

  const filteredPresets = dataSharePresetsStore.getFilteredPresetsByFeeId(
    props.feedId,
    props.sourceId,
  );
  return dataSharePresetsStore.convertPresetsToOptions(
    filteredPresets,
    props.feedId,
    props.sourceId,
  );
});

const presetOptions = computed((): PresetOptionsGroup[] => {
  const baseOptions = basePresetOptions.value;

  if (!selectedSingleSharePreset.value) return baseOptions;

  return [
    ...baseOptions,
    {
      heading: null,
      items: [
        {
          value: selectedSingleSharePreset.value.id,
          label: 'Custom Field Selection',
          presetType: 'single_share_preset',
        },
      ],
    },
  ];
});

const shouldShowPresetSelector = computed(() => {
  return (
    localVisibility.value === SHARING ||
    localVisibility.value === GREENFIELD_SHARING
  );
});

function handleCheckboxChange(partnerId: number, checked: boolean) {
  emit('checkbox-change', partnerId, checked);
}

function handleVisibilityChange(partnerId: number, visibility: SharingType) {
  localVisibility.value = visibility;
  emit('visibility-change', partnerId, visibility);
}

function handlePresetChange(presetId: number | undefined) {
  localSelectedPresetId.value = presetId ?? null;
  emit('preset-change', props.item.id, presetId);
}

function handleOpenPresetModal(presetId: number | undefined) {
  if (!props.feedId) return;
  openModal({
    feedId: props.feedId,
    presetId,
    sourceId: props.sourceId,
    isForPartner: true,
    onSuccess: (newPresetId: number, isNewPreset: boolean) => {
      if (isNewPreset) {
        localSelectedPresetId.value = newPresetId;
        handlePresetChange(newPresetId);
      }
    },
  });
}

function handleOpenPartnerSettings(overridesFilter?: boolean) {
  emit('open-partner-settings', props.item.id, overridesFilter);
}

function isPartnerSelected(partnerId: number): boolean {
  return props.selectedPartnerIdsSet.has(partnerId);
}

function handleRestoreToDefault() {
  emit('restore-to-default', props.item.id);
}

const selectCheckboxLabel = computed(() => `Select ${props.item.name}`);
const sharingLevelLabel = computed(() => `Sharing Level, ${props.item.name}`);
const fieldPresetsLabel = computed(() => `Field Presets, ${props.item.name}`);

const { isHovered, nameCellStyle } = useStickyNameCell({
  hasChanges: toRef(() => props.hasChanges ?? false),
});
</script>
<style lang="pcss" scoped>
@reference "../../../css/theme.css";
.c-population-tab-partners-row__tag {
  @apply text-neutral-text text-sm p-4;
}

.c-population-tab-partners-row--modified {
  background-color: rgba(221, 222, 255, 0.25);
}

.c-population-tab-partners-row__name-cell {
  @apply sticky left-0 z-[2];
  margin-left: -4px;
  padding-left: 16px;
}
</style>
